import { doc, getDoc, setDoc, increment, serverTimestamp } from "firebase/firestore";

/**
 * Dispositivos confiables por perfil (PIN).
 * Autorizacion de comodidad: evita repetir el PIN en un dispositivo ya
 * autenticado por el usuario, mientras el PIN no cambie y no se revoque.
 * No pretende resistir a un usuario tecnico con acceso al navegador.
 *
 * Rutas Firestore:
 * - workspaces/{workspaceId}/settings/trustedDevices/profiles/{profileId}
 *     { trustedDevicesVersion, updatedAt }
 * - workspaces/{workspaceId}/settings/trustedDevices/profiles/{profileId}/tokens/{hashId}
 *     { tokenHash, createdAt, pinUpdatedAtSnapshot, trustedDevicesVersionSnapshot }
 *
 * El token del dispositivo se guarda SOLO localmente. En Firestore unicamente
 * se guarda su hash SHA-256 (hashId = nombre del documento = tokenHash).
 */

const LOCAL_KEY_PREFIX = 'rutapp_trusted_device_';
const DEFAULT_VERSION = 0;

export function normalizePinUpdatedAt(pinUpdatedAt) {
  return String(pinUpdatedAt?.seconds ?? pinUpdatedAt ?? '');
}

export async function generateDeviceToken() {
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error('Web Crypto API no disponible en este navegador');
  }
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return bufferToHex(bytes);
}

export async function sha256Hex(text) {
  if (!globalThis.crypto?.subtle?.digest) {
    throw new Error('Web Crypto SubtleCrypto no disponible en este navegador');
  }
  const data = new TextEncoder().encode(text);
  const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
  return bufferToHex(new Uint8Array(hashBuffer));
}

function bufferToHex(buffer) {
  return Array.from(buffer).map(b => b.toString(16).padStart(2, '0')).join('');
}

function getLocalKey(workspaceId, profileId) {
  return LOCAL_KEY_PREFIX + workspaceId + "_" + profileId;
}

export function getLocalDeviceToken(workspaceId, profileId) {
  try {
    return globalThis.localStorage?.getItem(getLocalKey(workspaceId, profileId)) || null;
  } catch (error) {
    console.warn('[TRUSTED_DEVICE] Error reading local token:', error);
    return null;
  }
}

export function setLocalDeviceToken(workspaceId, profileId, token) {
  try {
    globalThis.localStorage?.setItem(getLocalKey(workspaceId, profileId), token);
  } catch (error) {
    console.warn('[TRUSTED_DEVICE] Error saving local token:', error);
  }
}

export function removeLocalDeviceToken(workspaceId, profileId) {
  try {
    globalThis.localStorage?.removeItem(getLocalKey(workspaceId, profileId));
  } catch (error) {
    console.warn('[TRUSTED_DEVICE] Error removing local token:', error);
  }
}

function profileDocRef(db, workspaceId, profileId) {
  return doc(db, 'workspaces', workspaceId, 'settings', 'trustedDevices', 'profiles', profileId);
}

function tokenDocRef(db, workspaceId, profileId, hashId) {
  return doc(db, 'workspaces', workspaceId, 'settings', 'trustedDevices', 'profiles', profileId, 'tokens', hashId);
}

export async function getTrustedDevicesVersion(workspaceId, db, profileId) {
  if (!workspaceId || !db || !profileId) return DEFAULT_VERSION;
  try {
    const snap = await getDoc(profileDocRef(db, workspaceId, profileId));
    if (snap.exists()) {
      return snap.data()?.trustedDevicesVersion ?? DEFAULT_VERSION;
    }
    return DEFAULT_VERSION;
  } catch (error) {
    console.error('[TRUSTED_DEVICE] Error reading version:', error);
    return DEFAULT_VERSION;
  }
}

export async function registerTrustedDevice(workspaceId, db, profileId, pinUpdatedAt) {
  if (!workspaceId || !db || !profileId) return false;
  try {
    const token = await generateDeviceToken();
    const hashId = await sha256Hex(token);
    const version = await getTrustedDevicesVersion(workspaceId, db, profileId);

    await setDoc(tokenDocRef(db, workspaceId, profileId, hashId), {
      tokenHash: hashId,
      createdAt: serverTimestamp(),
      pinUpdatedAtSnapshot: normalizePinUpdatedAt(pinUpdatedAt),
      trustedDevicesVersionSnapshot: version,
    });

    setLocalDeviceToken(workspaceId, profileId, token);
    return true;
  } catch (error) {
    console.error('[TRUSTED_DEVICE] Error registering device:', error);
    return false;
  }
}

export async function validateTrustedDevice(workspaceId, db, profileId, pinUpdatedAt) {
   // [TRUSTED_DEVICE_DIAG] Stage 1: Check parameters
   console.log('[TRUSTED_DEVICE_DIAG] Stage 1 - Parameters:', {
     workspaceId,
     profileId,
     hasDb: !!db,
   });
   if (!workspaceId || !db || !profileId) {
     console.log('[TRUSTED_DEVICE_DIAG] FAIL: Missing parameter (workspaceId, db, or profileId)');
     return false;
   }

   // [TRUSTED_DEVICE_DIAG] Stage 2: Check local token
   const localKey = getLocalKey(workspaceId, profileId);
   const token = getLocalDeviceToken(workspaceId, profileId);
   console.log('[TRUSTED_DEVICE_DIAG] Stage 2 - Local token:', {
     localKey,
     tokenExists: !!token,
     tokenPrefix: token ? token.substring(0, 6) : null,
   });
   if (!token) {
     console.log('[TRUSTED_DEVICE_DIAG] FAIL: No local token found');
     return false;
   }

   try {
     // [TRUSTED_DEVICE_DIAG] Stage 3: Check Firestore document
     const hashId = await sha256Hex(token);
     const fsPath = `workspaces/${workspaceId}/settings/trustedDevices/profiles/${profileId}/tokens/${hashId}`;
     const snap = await getDoc(tokenDocRef(db, workspaceId, profileId, hashId));
     console.log('[TRUSTED_DEVICE_DIAG] Stage 3 - Firestore token doc:', {
       fsPath,
       docExists: snap.exists(),
       hashIdPrefix: hashId.substring(0, 6),
     });

     if (!snap.exists()) {
       console.log('[TRUSTED_DEVICE_DIAG] FAIL: Token document does not exist in Firestore');
       removeLocalDeviceToken(workspaceId, profileId);
       return false;
     }

     // [TRUSTED_DEVICE_DIAG] Stage 4: Compare PIN and version
     const data = snap.data() || {};
     const currentVersion = await getTrustedDevicesVersion(workspaceId, db, profileId);
     const currentPinSignature = normalizePinUpdatedAt(pinUpdatedAt);

     console.log('[TRUSTED_DEVICE_DIAG] Stage 4 - PIN and version check:', {
       pinUpdatedAtSnapshot: data.pinUpdatedAtSnapshot,
       currentPinSignature,
       pinMatches: data.pinUpdatedAtSnapshot === currentPinSignature,
       trustedDevicesVersionSnapshot: data.trustedDevicesVersionSnapshot,
       currentVersion,
       versionMatches: data.trustedDevicesVersionSnapshot === currentVersion,
     });

     const pinMatches = data.pinUpdatedAtSnapshot === currentPinSignature;
     const versionMatches = data.trustedDevicesVersionSnapshot === currentVersion;

     if (pinMatches && versionMatches) {
       console.log('[TRUSTED_DEVICE_DIAG] SUCCESS: Device is trusted');
       return true;
     }

     console.log('[TRUSTED_DEVICE_DIAG] FAIL: PIN or version mismatch');
     removeLocalDeviceToken(workspaceId, profileId);
     return false;
   } catch (error) {
     console.error('[TRUSTED_DEVICE] Error validating device:', error);
     console.log('[TRUSTED_DEVICE_DIAG] FAIL: Exception thrown');
     return false;
   }
 }

export async function revokeTrustedDevices(workspaceId, db, profileId) {
  if (!workspaceId || !db || !profileId) return false;
  try {
    await setDoc(profileDocRef(db, workspaceId, profileId), {
      trustedDevicesVersion: increment(1),
      updatedAt: serverTimestamp(),
    }, { merge: true });

    removeLocalDeviceToken(workspaceId, profileId);
    return true;
  } catch (error) {
    console.error('[TRUSTED_DEVICE] Error revoking devices:', error);
    return false;
  }
}